/**
 * useGardenMotion — the single reduced-motion gate for the whole app.
 *
 * Wraps framer-motion's `useReducedMotion()` (coalescing its initial `null`
 * to `false`, the idiom proven in chat/MessageList) and returns motion props
 * that are ALREADY gated: when the user prefers reduced motion, every helper
 * returns `{}` so elements mount in their final state with no transform/opacity
 * tween. Baking the gate in here makes it impossible for a call site to forget
 * (the house-style gap the blueprint flagged).
 *
 * Focus rings are intentionally NOT gated on reduced motion — they are an a11y
 * affordance, handled with Tailwind `focus-visible:` utilities at call sites.
 */
import { useReducedMotion, type MotionProps } from "framer-motion";

import {
  MOTION,
  containerVariants,
  itemVariants,
} from "@/lib/motion";

export interface GardenMotion {
  /** True when the user prefers reduced motion. */
  reduced: boolean;
  /**
   * Entrance props for a `<motion.*>` element. `'container'` orchestrates a
   * stagger over children; `'item'` (default) is a single fade+rise. Returns
   * an empty `MotionProps` ({}) under reduced motion.
   */
  entrance: (kind?: "container" | "item") => MotionProps;
  /** Press-feedback props (whileTap scale) for a tier. `whileTap` is absent under reduced motion. */
  press: (tier: "surface" | "cta" | "navTab") => { whileTap?: { scale: number } };
  /** Overlay/sheet enter+exit variant labels. {} under reduced motion. */
  overlay: () => MotionProps;
}

export function useGardenMotion(): GardenMotion {
  const reduced = useReducedMotion() ?? false;

  return {
    reduced,
    entrance: (kind = "item") => {
      if (reduced) return {};
      return {
        variants: kind === "container" ? containerVariants : itemVariants,
        initial: "hidden",
        animate: "show",
      } satisfies MotionProps;
    },
    press: (tier) => (reduced ? {} : { whileTap: { scale: MOTION.press[tier] } }),
    overlay: () =>
      reduced
        ? {}
        : ({ initial: "hidden", animate: "show", exit: "exit" } satisfies MotionProps),
  };
}
